import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, DatePicker, Select, Space,
  Tag, Tooltip, Popconfirm, message, Row, Col,
  Typography, Segmented, Spin, Empty, Switch, InputNumber,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  CalendarOutlined, UnorderedListOutlined, CarOutlined,
  EnvironmentOutlined, UserOutlined, ClockCircleOutlined,
  LeftOutlined, RightOutlined, SearchOutlined, NumberOutlined,
  MergeOutlined, TeamOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import isBetween from 'dayjs/plugin/isBetween';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { businessTripAPI, masterAPI, scheduleAPI } from '../services/api';
import { getUsername } from '../services/authToken';
import { brand, gray, state, modalWidth } from '../theme/tokens';
import { usePermission } from '../contexts/PermissionContext';
import { useFitTableHeight, useFillHeight } from '../hooks/useViewportFit';

dayjs.extend(isBetween);
dayjs.extend(weekOfYear);
dayjs.locale('ko');

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface BusinessTrip {
  id: number;
  trip_date_raw?: string;
  trip_start_date?: string;
  trip_end_date?: string;
  researchers?: string;
  work_type?: string;
  test_number?: string;
  work_content?: string;
  destination?: string;
  vehicle_number?: string;
  duration?: string;
  supervisor_confirmed_at?: string;
  note?: string;
  created_by?: number;
  created_at?: string;
}

// 구분(work_type) 정의
const WORK_TYPES = [
  { value: '외근',     label: '외근',     icon: '🚗', group: 'out' },
  { value: '오전외근', label: '오전외근', icon: '🚗', group: 'out' },
  { value: '오후외근', label: '오후외근', icon: '🚗', group: 'out' },
  { value: '내근',     label: '내근',     icon: '🏢', group: 'in' },
  { value: '오전내근', label: '오전내근', icon: '🏢', group: 'in' },
  { value: '오후내근', label: '오후내근', icon: '🏢', group: 'in' },
  { value: '연차',     label: '연차',     icon: '😴', group: 'leave' },
  { value: '오전반차', label: '오전반차', icon: '😴', group: 'leave' },
  { value: '오후반차', label: '오후반차', icon: '😴', group: 'leave' },
];

const WORK_TYPE_COLOR: Record<string, { bg: string; border: string; text: string }> = {
  // 외근 그룹 (indigo — 오전=극연, 전일=표준, 오후=극진)
  '오전외근': { bg: '#f5f3ff', border: '#c4b5fd', text: '#818cf8' },
  '외근':     { bg: '#e0e7ff', border: '#6366f1', text: '#4338ca' },
  '오후외근': { bg: '#a5b4fc', border: '#3730a3', text: '#1e1b4b' },
  // 내근 그룹 (green — 오전=극연, 전일=표준, 오후=극진)
  '오전내근': { bg: '#f0fdf4', border: '#86efac', text: '#22c55e' },
  '내근':     { bg: '#dcfce7', border: '#22c55e', text: '#15803d' },
  '오후내근': { bg: '#86efac', border: '#15803d', text: '#14532d' },
  // 연차 그룹 (purple — 오전=극연, 전일=표준, 오후=극진)
  '오전반차': { bg: '#faf5ff', border: '#e9d5ff', text: '#c084fc' },
  '연차':     { bg: '#f3e8ff', border: '#a855f7', text: '#9333ea' },
  '오후반차': { bg: '#d8b4fe', border: '#6d28d9', text: '#4c1d95' },
  // 그룹 기본값
  'out':   { bg: '#e0e7ff', border: '#6366f1', text: '#4338ca' },
  'in':    { bg: '#dcfce7', border: '#22c55e', text: '#15803d' },
  'leave': { bg: '#f3e8ff', border: '#a855f7', text: '#9333ea' },
};

function getWorkTypeColor(wt: string | undefined) {
  return WORK_TYPE_COLOR[wt ?? '외근'] ?? WORK_TYPE_COLOR['out'];
}

function getWorkTypeGroup(wt: string | undefined): string {
  return WORK_TYPES.find(t => t.value === wt)?.group || 'out';
}

// 외근 계열인지 (출장지·차량·시험번호 표시 여부)
function isOutdoorType(wt: string | undefined): boolean {
  if (!wt) return true; // 기본값: 외근
  return getWorkTypeGroup(wt) === 'out';
}

// 연구원별 색상
const RESEARCHER_COLORS = [
  '#4f46e5', '#0891b2', '#059669', '#d97706', '#dc2626',
  '#7c3aed', '#be185d', '#0369a1', '#15803d', '#b45309',
];

const DURATION_COLORS: Record<string, string> = {
  '종일': 'purple',
  '오전': 'blue',
  '오후': 'orange',
};

function getResearcherColor(name: string, allResearchers: string[]): string {
  const idx = allResearchers.indexOf(name) % RESEARCHER_COLORS.length;
  return RESEARCHER_COLORS[Math.max(0, idx)];
}

function getAllResearchers(trips: BusinessTrip[]): string[] {
  const set = new Set<string>();
  trips.forEach(t => {
    if (t.researchers) {
      t.researchers.split(/[,，]/).forEach(r => set.add(r.trim()));
    }
  });
  return Array.from(set).filter(Boolean);
}

// 연구원 주간 현황 뷰
const ResearcherView: React.FC<{
  trips: BusinessTrip[];
  weekStart: Dayjs;
  researchers: string[];
  allResearchers: string[];
  onTripClick: (trip: BusinessTrip) => void;
}> = ({ trips, weekStart, researchers, allResearchers, onTripClick }) => {
  const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
  const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));
  const today = dayjs();

  const WORK_TYPE_ORDER: Record<string, number> = {
    '오전외근': 0, '오전내근': 0, '오전반차': 0,
    '외근': 1, '내근': 1, '연차': 1,
    '오후외근': 2, '오후내근': 2, '오후반차': 2,
  };

  const getTripsForCell = (researcher: string, day: Dayjs): BusinessTrip[] =>
    trips
      .filter(t => {
        if (!t.trip_start_date || !t.researchers) return false;
        if (!t.researchers.split(/[,，]/).map(r => r.trim()).includes(researcher)) return false;
        const start = dayjs(t.trip_start_date).startOf('day');
        const end = t.trip_end_date ? dayjs(t.trip_end_date).endOf('day') : start.endOf('day');
        return day.isBetween(start, end, 'day', '[]');
      })
      .sort((a, b) => (WORK_TYPE_ORDER[a.work_type ?? ''] ?? 1) - (WORK_TYPE_ORDER[b.work_type ?? ''] ?? 1));



  return (
    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: `1px solid ${gray[200]}` }}>
      {/* 범례 */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: gray[600] }}>범례</span>
        {([
          ['오전외근', '외근', '오후외근'],
          ['오전내근', '내근', '오후내근'],
          ['오전반차', '연차', '오후반차'],
        ] as string[][]).map((types, gi) => (
          <span key={gi} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            {types.map(wt => {
              const c = WORK_TYPE_COLOR[wt];
              return (
                <span key={wt} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: c.bg, border: `1.5px solid ${c.border}` }} />
                  <span style={{ color: gray[500] }}>{wt}</span>
                </span>
              );
            })}
          </span>
        ))}
      </div>

      {/* 테이블 */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 72 }} />
            {days.map((day) => <col key={day.format('YYYY-MM-DD')} style={{ width: `calc((100% - 72px) / 7)` }} />)}
          </colgroup>
          <thead>
            <tr>
              <th style={{ padding: '10px 8px', fontSize: 12, fontWeight: 700, color: gray[600], textAlign: 'center', border: `1px solid ${gray[200]}`, background: gray[50] }}>
                성명
              </th>
              {days.map((day) => {
                const isToday = day.isSame(today, 'day');
                const isSun = day.day() === 0;
                const isSat = day.day() === 6;
                return (
                  <th key={day.format('YYYY-MM-DD')} style={{
                    padding: '6px 4px', textAlign: 'center', border: `1px solid ${gray[200]}`,
                    background: isToday ? '#eff6ff' : gray[50],
                  }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                      padding: isToday ? '2px 6px' : 0,
                      borderRadius: isToday ? 10 : 0,
                      background: isToday ? brand.primary : 'transparent',
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: isToday ? '#fff' : isSun ? '#ef4444' : isSat ? '#3b82f6' : '#64748b' }}>
                        {WEEKDAYS[day.day()]}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: isToday ? 700 : 500, color: isToday ? '#fff' : isSun ? '#ef4444' : isSat ? '#3b82f6' : '#1e293b' }}>
                        {day.date()}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {researchers.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: 40, textAlign: 'center', color: gray[400], border: `1px solid ${gray[200]}`, fontSize: 13 }}>
                  연구원 정보가 없습니다.
                </td>
              </tr>
            ) : researchers.map((researcher, rIdx) => (
              <tr key={researcher} style={{ background: rIdx % 2 === 0 ? '#eef2ff' : gray[50] }}>
                <td style={{
                  padding: '8px 6px', textAlign: 'center', fontSize: 12, fontWeight: 700,
                  color: getResearcherColor(researcher, allResearchers.length > 0 ? allResearchers : researchers),
                  border: `1px solid ${gray[200]}`, whiteSpace: 'nowrap', verticalAlign: 'middle',
                }}>
                  {researcher}
                </td>
                {days.map((day) => {
                  const cellTrips = getTripsForCell(researcher, day);
                  const isToday = day.isSame(today, 'day');
                  return (
                    <td key={day.format('YYYY-MM-DD')} style={{
                      padding: '4px', border: `1px solid ${gray[200]}`, verticalAlign: 'top', overflow: 'hidden',
                      background: isToday ? '#fafeff' : 'transparent',
                    }}>
                      {cellTrips.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {cellTrips.map(trip => {
                            const c = getWorkTypeColor(trip.work_type);
                            const icon = WORK_TYPES.find(t => t.value === trip.work_type)?.icon || '🚗';
                            const isOut = isOutdoorType(trip.work_type);
                            return (
                              <Tooltip key={trip.id} title={
                                <div>
                                  <div style={{ fontWeight: 700 }}>{trip.work_type}{trip.researchers && trip.researchers.split(/[,，]/).length > 1 ? ` (${trip.researchers})` : ''}</div>
                                  {trip.destination && <div>📍 {trip.destination}</div>}
                                  {trip.work_content && <div>{trip.work_content}</div>}
                                  {trip.vehicle_number && <div>🚗 {trip.vehicle_number}</div>}
                                  {trip.note && <div style={{ color: gray[400] }}>{trip.note}</div>}
                                </div>
                              }>
                                <div
                                  onClick={() => onTripClick(trip)}
                                  style={{
                                    background: c.bg, border: `1px solid ${c.border}`,
                                    borderRadius: 4, padding: '3px 5px', cursor: 'pointer',
                                    fontSize: 11, color: c.text, lineHeight: 1.5,
                                  }}
                                >
                                  <div style={{ fontWeight: 600 }}>{icon} {trip.work_type}</div>
                                  {isOut && trip.destination && (
                                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      📍 {trip.destination}
                                    </div>
                                  )}
                                  {trip.work_content && (
                                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: gray[500], fontSize: 10 }}>
                                      {trip.work_content}
                                    </div>
                                  )}
                                </div>
                              </Tooltip>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ minHeight: 36 }} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
};

// 달력 뷰 컴포넌트
const CalendarView: React.FC<{
  trips: BusinessTrip[];
  refDate: Dayjs;
  calSubMode: 'month' | '1week' | '2week';
  allResearchers: string[];
  onDayClick: (date: Dayjs) => void;
  onTripClick: (trip: BusinessTrip) => void;
}> = ({ trips, refDate, calSubMode, allResearchers, onDayClick, onTripClick }) => {
  const days: Dayjs[] = (() => {
    if (calSubMode === '1week') {
      const s = refDate.startOf('week');
      return Array.from({ length: 7 }, (_, i) => s.add(i, 'day'));
    }
    if (calSubMode === '2week') {
      const s = refDate.startOf('week');
      return Array.from({ length: 14 }, (_, i) => s.add(i, 'day'));
    }
    // month
    const start = refDate.startOf('month').startOf('week');
    const end = refDate.endOf('month').endOf('week');
    const result: Dayjs[] = [];
    let cur = start;
    while (cur.isBefore(end) || cur.isSame(end, 'day')) { result.push(cur); cur = cur.add(1, 'day'); }
    return result;
  })();
  const rowCount = days.length / 7;
  const minCellHeight = calSubMode === 'month'
    ? 100
    : `calc((100vh - 320px) / ${rowCount})`;

  const tripsForDay = (day: Dayjs): BusinessTrip[] => {
    return trips.filter(t => {
      if (!t.trip_start_date) return false;
      const start = dayjs(t.trip_start_date).startOf('day');
      const end = t.trip_end_date ? dayjs(t.trip_end_date).endOf('day') : start.endOf('day');
      return day.isBetween(start, end, 'day', '[]');
    });
  };

  const today = dayjs();
  const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

  return (
    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: `1px solid ${gray[200]}` }}>
      {/* 요일 헤더 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: gray[50], borderBottom: '1px solid #e2e8f0' }}>
        {WEEKDAYS.map((d, i) => (
          <div key={d} style={{
            textAlign: 'center', padding: '10px 0', fontSize: 12, fontWeight: 700,
            color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : '#64748b',
          }}>{d}</div>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {days.map((day, idx) => {
          const isCurrentMonth = calSubMode === 'month' ? day.month() === refDate.month() : true;
          const isToday = day.isSame(today, 'day');
          const dayTrips = tripsForDay(day);
          const isSun = day.day() === 0;
          const isSat = day.day() === 6;

          return (
            <div
              key={day.format('YYYY-MM-DD')}
              onClick={() => onDayClick(day)}
              style={{
                minHeight: minCellHeight,
                borderRight: (idx + 1) % 7 !== 0 ? '1px solid #f1f5f9' : 'none',
                borderBottom: '1px solid #f1f5f9',
                padding: '6px 4px 4px',
                cursor: 'pointer',
                background: isToday ? '#eff6ff' : !isCurrentMonth ? '#fafafa' : '#fff',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (!isToday) (e.currentTarget as HTMLElement).style.background = gray[50]; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isToday ? '#eff6ff' : !isCurrentMonth ? '#fafafa' : '#fff'; }}
            >
              <div style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, borderRadius: '50%', marginBottom: 4,
                background: isToday ? brand.primary : 'transparent',
                color: isToday ? '#fff' : isSun ? '#ef4444' : isSat ? '#3b82f6' : isCurrentMonth ? '#1e293b' : '#cbd5e1',
                fontSize: 12, fontWeight: isToday ? 700 : 500,
              }}>
                {day.date()}
              </div>

              {dayTrips.slice(0, calSubMode === 'month' ? 3 : 8).map(trip => {
                const mainResearcher = trip.researchers?.split(/[,，]/)[0].trim() || '';
                const color = getResearcherColor(mainResearcher, allResearchers);
                return (
                  <Tooltip
                    key={trip.id}
                    title={
                      <div>
                        <div style={{ fontWeight: 700 }}>{trip.work_type || ''} {trip.researchers}</div>
                        <div>{trip.work_content}</div>
                        <div>{trip.destination}{trip.vehicle_number ? ` · 차량 ${trip.vehicle_number}` : ''}</div>
                        {trip.duration && <div>{trip.duration}</div>}
                      </div>
                    }
                    placement="top"
                  >
                    <div
                      onClick={e => { e.stopPropagation(); onTripClick(trip); }}
                      style={{
                        background: color + '18',
                        borderLeft: `3px solid ${color}`,
                        borderRadius: 4,
                        padding: '1px 5px',
                        marginBottom: 2,
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                        color: gray[800],
                      }}
                    >
                      {trip.work_type && <span style={{ marginRight: 2 }}>{WORK_TYPES.find(t => t.value === trip.work_type)?.icon}</span>}
                      <span style={{ color, fontWeight: 700, marginRight: 3 }}>{mainResearcher}</span>
                      {trip.destination || trip.work_type}
                    </div>
                  </Tooltip>
                );
              })}
              {dayTrips.length > (calSubMode === 'month' ? 3 : 8) && (
                <div style={{ fontSize: 10, color: gray[400], paddingLeft: 5 }}>
                  +{dayTrips.length - (calSubMode === 'month' ? 3 : 8)}건
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 메인 컴포넌트
const BusinessTripPage: React.FC = () => {
  const { hasPermission, roles } = usePermission();
  const canWrite = hasPermission('business_trips', 'WRITE');
  const canDelete = hasPermission('business_trips', 'DELETE');
  const canConfirm = roles.some(r => r.role_name.includes('관리자') || r.role_name.includes('선임'));

  const [trips, setTrips] = useState<BusinessTrip[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'calendar' | 'list' | 'researcher'>('list');
  /* 목록 뷰: 표 본문 높이 실측 — 페이지 세로스크롤 없이 페이지네이션까지 뷰포트 안에 */
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  /* 달력/연구원 뷰: 내용이 길면 뷰 내부에서만 스크롤 (문서 스크롤 방지) */
  const { ref: calWrapRef, height: calHeight } = useFillHeight(360);
  const { ref: resWrapRef, height: resHeight } = useFillHeight(360);
  const [researcherWeek, setResearcherWeek] = useState(dayjs().startOf('week'));
  const [calSubMode, setCalSubMode] = useState<'month' | '1week' | '2week'>('month');
  const [listGroupMode, setListGroupMode] = useState<'all' | 'monthly' | 'weekly'>('monthly');
  /* 페이지당 건수 — 전체 목록은 표준 20, 월별/주별은 그 기간 전체가 한 화면에 보이게 50 */
  const [listPageSize, setListPageSize] = useState(50);
  useEffect(() => { setListPageSize(listGroupMode === 'all' ? 20 : 50); }, [listGroupMode]);
  const [masterResearchers, setMasterResearchers] = useState<string[]>([]);
  const [masterVehicles, setMasterVehicles] = useState<string[]>([]);
  const [scheduleOptions, setScheduleOptions] = useState<{ value: string; label: string; location?: string }[]>([]);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [currentWeek, setCurrentWeek] = useState(dayjs().startOf('week'));
  const [searchYear, setSearchYear] = useState<number>(dayjs().year());
  const [researcherInput, setResearcherInput] = useState('');
  const [searchResearcher, setSearchResearcher] = useState('');
  const [searchDateRange, setSearchDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [searchWorkTypes, setSearchWorkTypes] = useState<string[]>();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<BusinessTrip | null>(null);
  const [defaultDate, setDefaultDate] = useState<Dayjs | null>(null);
  const [form] = Form.useForm();
  const watchedWorkType = Form.useWatch('work_type', form);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingTrip, setViewingTrip] = useState<BusinessTrip | null>(null);

  const [testPickerOpen, setTestPickerOpen] = useState(false);
  const [testPickerSearch, setTestPickerSearch] = useState('');
  const [testPickerSelected, setTestPickerSelected] = useState<string[]>([]);
  const [scheduleList, setScheduleList] = useState<any[]>([]);

  const [selectedTestNumbers, setSelectedTestNumbers] = useState<string[]>([]);
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatCount, setRepeatCount] = useState(2);
  const [repeatInterval, setRepeatInterval] = useState(7);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const keepOpenRef = React.useRef(false);


  // 연구원 검색 debounce (400ms)
  useEffect(() => {
    const t = setTimeout(() => setSearchResearcher(researcherInput), 400);
    return () => clearTimeout(t);
  }, [researcherInput]);

  const loadTrips = useCallback(async () => {
    setLoading(true);
    try {
      const [dateFrom, dateTo] = searchDateRange;
      const params: Record<string, any> = {
        researcher: searchResearcher || undefined,
        work_types: searchWorkTypes && searchWorkTypes.length > 0 ? searchWorkTypes.join(',') : undefined,
      };
      if (dateFrom && dateTo) {
        params.date_from = dateFrom.format('YYYY-MM-DD');
        params.date_to = dateTo.format('YYYY-MM-DD');
      } else {
        params.year = searchYear || undefined;
      }
      const res = await businessTripAPI.list(params);
      setTrips(res.data);
    } catch {
      message.error('출장 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [searchYear, searchResearcher, searchDateRange, searchWorkTypes]);

  useEffect(() => { loadTrips(); }, [loadTrips]);

  useEffect(() => {
    masterAPI.researchers.list().then(r => {
      const sorted = [...r.data].sort((a: any, b: any) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      setMasterResearchers(sorted.map((x: any) => x.name));
    }).catch(() => {});
    masterAPI.vehicles.list().then(r => setMasterVehicles(r.data.map((x: any) => x.vehicle_number))).catch(() => {});
    scheduleAPI.list({}).then(r => {
      const raw = r.data.schedules || r.data || [];
      setScheduleList(raw);
      const opts = raw.map((s: any) => ({
        value: s.test_number || '',
        label: `${s.test_number || ''} ${s.title ? `· ${s.title}` : ''}`.trim(),
        location: s.location_name || '',
      }));
      setScheduleOptions(opts);
    }).catch(() => {});
  }, []);

  const allResearchers = useMemo(() => getAllResearchers(trips), [trips]);

  const researcherWeekTrips = useMemo(() => {
    const ws = researcherWeek.startOf('week');
    const we = researcherWeek.endOf('week');
    return trips.filter(t => {
      if (!t.trip_start_date) return false;
      const start = dayjs(t.trip_start_date).startOf('day');
      const end = t.trip_end_date ? dayjs(t.trip_end_date).endOf('day') : start.endOf('day');
      return !start.isAfter(we, 'day') && !end.isBefore(ws, 'day');
    });
  }, [trips, researcherWeek]);

  const listTrips = useMemo(() => {
    const tripDate = (t: BusinessTrip) =>
      t.trip_start_date ? dayjs(t.trip_start_date) : (t.trip_date_raw ? dayjs(t.trip_date_raw) : null);
    if (listGroupMode === 'monthly') {
      return [...trips]
        .filter(t => { const d = tripDate(t); return d && d.year() === currentMonth.year() && d.month() === currentMonth.month(); })
        .sort((a, b) => (b.trip_start_date || b.trip_date_raw || '').localeCompare(a.trip_start_date || a.trip_date_raw || ''));
    }
    if (listGroupMode === 'weekly') {
      const ws = currentWeek.startOf('week');
      const we = currentWeek.endOf('week');
      return [...trips]
        .filter(t => { const d = tripDate(t); return d && !d.isBefore(ws, 'day') && !d.isAfter(we, 'day'); })
        .sort((a, b) => (b.trip_start_date || b.trip_date_raw || '').localeCompare(a.trip_start_date || a.trip_date_raw || ''));
    }
    return trips;
  }, [trips, listGroupMode, currentMonth, currentWeek]);

  // 날짜 변경 기준 그룹 인덱스 — 같은 날짜=같은 색, 날짜 바뀌면 색 전환
  const dateGroupMap = useMemo(() => {
    const map = new Map<number, number>();
    let cur = '';
    let idx = -1;
    for (const trip of listTrips) {
      const d = trip.trip_start_date
        ? dayjs(trip.trip_start_date).format('YYYY-MM-DD')
        : (trip.trip_date_raw || '');
      if (d !== cur) { cur = d; idx++; }
      map.set(trip.id, idx);
    }
    return map;
  }, [listTrips]);

  const calendarTrips = useMemo(() => {
    if (calSubMode === 'month') {
      return trips.filter(t => {
        if (!t.trip_start_date) return false;
        const start = dayjs(t.trip_start_date);
        return start.year() === currentMonth.year() && start.month() === currentMonth.month();
      });
    }
    const weekStart = currentWeek.startOf('week');
    const weekEnd = (calSubMode === '2week' ? weekStart.add(13, 'day') : weekStart.add(6, 'day')).endOf('day');
    return trips.filter(t => {
      if (!t.trip_start_date) return false;
      const start = dayjs(t.trip_start_date);
      return (start.isAfter(weekStart) || start.isSame(weekStart, 'day')) &&
             (start.isBefore(weekEnd) || start.isSame(weekEnd, 'day'));
    });
  }, [trips, currentMonth, currentWeek, calSubMode]);

  const openCreate = (date?: Dayjs) => {
    setEditingTrip(null);
    setDefaultDate(date || null);
    setSelectedTestNumbers([]);
    setRepeatEnabled(false);
    setRepeatCount(2);
    setRepeatInterval(7);
    form.resetFields();
    const loginUser = getUsername() || '';
    form.setFieldsValue({
      ...(date ? { date_range: [date, date] } : {}),
      researchers: loginUser ? [loginUser] : [],
    });
    setModalOpen(true);
  };

  const openEdit = (trip: BusinessTrip) => {
    setEditingTrip(trip);
    setDefaultDate(null);
    setRepeatEnabled(false);
    const testNums = trip.test_number
      ? trip.test_number.split(/[,，]/).map(t => t.trim()).filter(Boolean)
      : [];
    setSelectedTestNumbers(testNums);
    const researcherArr = trip.researchers
      ? trip.researchers.split(/[,，]/).map(r => r.trim()).filter(Boolean)
      : [];
    const vehicleArr = trip.vehicle_number
      ? trip.vehicle_number.split(/[,，]/).map(v => v.trim()).filter(Boolean)
      : [];
    form.setFieldsValue({
      researchers: masterResearchers.length > 0 ? researcherArr : trip.researchers,
      work_type: trip.work_type || '외근',
      vehicle_number: vehicleArr.length > 0 ? vehicleArr : (trip.vehicle_number ? [trip.vehicle_number] : []),
      work_content: trip.work_content,
      destination: trip.destination,
      duration: trip.duration,
      note: trip.note,
      date_range: [
        trip.trip_start_date ? dayjs(trip.trip_start_date) : null,
        trip.trip_end_date ? dayjs(trip.trip_end_date) : (trip.trip_start_date ? dayjs(trip.trip_start_date) : null),
      ],
      supervisor_confirmed_at: trip.supervisor_confirmed_at ? dayjs(trip.supervisor_confirmed_at) : null,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const dateRange: [Dayjs, Dayjs] | null = values.date_range;
      const start: Dayjs | null = dateRange?.[0] || null;
      const end: Dayjs | null = dateRange?.[1] || null;
      const researchers = Array.isArray(values.researchers)
        ? values.researchers.join(', ')
        : values.researchers;
      const vehicleNumber = Array.isArray(values.vehicle_number)
        ? values.vehicle_number.join(', ')
        : (values.vehicle_number || null);
      const testNumberStr = selectedTestNumbers.length > 0 ? selectedTestNumbers.join(', ') : null;

      const makePayload = (startDate: Dayjs | null, endDate: Dayjs | null) => ({
        ...values,
        researchers,
        vehicle_number: vehicleNumber,
        test_number: testNumberStr,
        trip_start_date: startDate ? startDate.format('YYYY-MM-DDTHH:mm:ss') : null,
        trip_end_date: endDate
          ? endDate.format('YYYY-MM-DDTHH:mm:ss')
          : startDate ? startDate.format('YYYY-MM-DDTHH:mm:ss') : null,
        supervisor_confirmed_at: values.supervisor_confirmed_at
          ? values.supervisor_confirmed_at.format('YYYY-MM-DDTHH:mm:ss') : null,
        trip_date_raw: startDate ? startDate.format('YYYY-MM-DD') : null,
      });

      if (editingTrip) {
        await businessTripAPI.update(editingTrip.id, makePayload(start, end));
        message.success('출장 정보가 수정되었습니다.');
      } else if (repeatEnabled && repeatCount > 1) {
        const payloads = Array.from({ length: repeatCount }, (_, i) => {
          const s = start ? start.add(i * repeatInterval, 'day') : null;
          const e = end ? end.add(i * repeatInterval, 'day') : null;
          return makePayload(s, e);
        });
        await businessTripAPI.bulkCreate(payloads);
        message.success(`${repeatCount}건이 등록되었습니다.`);
      } else {
        await businessTripAPI.create(makePayload(start, end));
        message.success('등록되었습니다.');
      }

      if (keepOpenRef.current) {
        keepOpenRef.current = false;
        form.resetFields();
        setSelectedTestNumbers([]);
        setRepeatEnabled(false);
      } else {
        setModalOpen(false);
      }
      loadTrips();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '저장에 실패했습니다.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await businessTripAPI.delete(id);
      message.success('삭제되었습니다.');
      loadTrips();
    } catch {
      message.error('삭제에 실패했습니다.');
    }
  };

  const handleConfirm = async (trip: BusinessTrip, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await businessTripAPI.update(trip.id, {
        supervisor_confirmed_at: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
      });
      message.success('확인일이 저장되었습니다.');
      loadTrips();
    } catch {
      message.error('저장에 실패했습니다.');
    }
  };

  const handleMerge = () => {
    const targets = trips.filter(t => selectedRowKeys.includes(t.id));
    if (targets.length < 2) return;
    const base = targets[0];
    const mergedContent = targets.map(t => t.work_content).filter(Boolean).join(' / ');
    const mergedDest = Array.from(new Set(targets.map(t => t.destination).filter(Boolean))).join(', ');
    const mergedResearchers = Array.from(new Set(
      targets.flatMap(t => (t.researchers || '').split(/[,，]/).map(r => r.trim())).filter(Boolean)
    )).join(', ');
    const mergedTestNums = Array.from(new Set(
      targets.flatMap(t => (t.test_number || '').split(/[,，]/).map(n => n.trim())).filter(Boolean)
    )).join(', ');

    Modal.confirm({
      title: `${targets.length}건을 하나로 통합하시겠습니까?`,
      content: (
        <div style={{ fontSize: 13, marginTop: 8 }}>
          <div><b>연구원:</b> {mergedResearchers || '-'}</div>
          <div><b>내용:</b> {mergedContent || '-'}</div>
          {mergedDest && <div><b>출장지:</b> {mergedDest}</div>}
          <div style={{ marginTop: 6, color: '#ef4444', fontSize: 12 }}>
            첫 번째 건만 남기고 나머지 {targets.length - 1}건은 삭제됩니다.
          </div>
        </div>
      ),
      okText: '통합',
      cancelText: '취소',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await businessTripAPI.update(base.id, {
            researchers: mergedResearchers,
            work_content: mergedContent,
            destination: mergedDest || base.destination,
            test_number: mergedTestNums || base.test_number,
          });
          for (const t of targets.slice(1)) {
            await businessTripAPI.delete(t.id);
          }
          message.success('통합되었습니다.');
          setSelectedRowKeys([]);
          loadTrips();
        } catch {
          message.error('통합에 실패했습니다.');
        }
      },
    });
  };


  const columns: ColumnsType<BusinessTrip> = [
    {
      title: '구분',
      dataIndex: 'work_type',
      width: 90,
      render: (v) => {
        const wt = WORK_TYPES.find(t => t.value === v);
        const colors = getWorkTypeColor(v);
        return v ? (
          <Tag style={{ fontSize: 11, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, margin: 0 }}>
            {wt?.icon} {v}
          </Tag>
        ) : <Tag style={{ fontSize: 11, margin: 0 }}>🚗 외근</Tag>;
      },
    },
    {
      title: '일자',
      dataIndex: 'trip_start_date',
      width: 120,
      render: (v, r) => {
        if (!v) return <Text type="secondary">{r.trip_date_raw || '-'}</Text>;
        const start = dayjs(v);
        const end = r.trip_end_date ? dayjs(r.trip_end_date) : start;
        if (start.isSame(end, 'day')) return start.format('YYYY-MM-DD');
        return `${start.format('YYYY-MM-DD')} ~ ${end.format('YYYY-MM-DD')}`;
      },
      sorter: (a, b) => (b.trip_start_date || '').localeCompare(a.trip_start_date || ''),
      defaultSortOrder: 'ascend',
    },
    {
      title: '연구원',
      dataIndex: 'researchers',
      width: 200,
      render: (v) => {
        if (!v) return '-';
        return v.split(/[,，]/).map((r: string) => r.trim()).filter(Boolean).map((r: string) => (
          <Tag key={r} color={getResearcherColor(r, allResearchers)} style={{ marginBottom: 2 }}>
            {r}
          </Tag>
        ));
      },
    },
    {
      title: '작업내용',
      dataIndex: 'work_content',
      ellipsis: true,
      render: (v) => v || '-',
    },
    {
      title: '출장지',
      dataIndex: 'destination',
      width: 160,
      render: (v) => v ? <><EnvironmentOutlined style={{ color: brand.primary, marginRight: 4 }} />{v}</> : '-',
    },
    {
      title: '차량',
      dataIndex: 'vehicle_number',
      width: 110,
      render: (v) => v ? <><CarOutlined style={{ marginRight: 3 }} />{v}</> : '-',
    },
    {
      title: '기간',
      dataIndex: 'duration',
      width: 70,
      render: (v) => v ? <Tag color={DURATION_COLORS[v] || 'default'}>{v}</Tag> : '-',
    },
    {
      title: '담당자확인',
      dataIndex: 'supervisor_confirmed_at',
      width: 90,
      render: (v, record) => v ? (
        <Tooltip title={`확인일: ${dayjs(v).format('YYYY-MM-DD')}`}>
          <Tag color="green" style={{ fontSize: 11, cursor: 'default', margin: 0 }}>
            ✓ {dayjs(v).format('YYYY-MM-DD')}
          </Tag>
        </Tooltip>
      ) : (
        canConfirm ? (
          <Button
            size="small"
            type="dashed"
            style={{ fontSize: 11, height: 22, padding: '0 8px', color: brand.primary, borderColor: brand.primary }}
            onClick={e => handleConfirm(record, e)}
          >
            확인
          </Button>
        ) : <Text type="secondary" style={{ fontSize: 11 }}>미확인</Text>
      ),
    },
    {
      title: '',
      width: 80,
      render: (_, record) => (
        <Space size={4}>
          {canWrite && (
            <Tooltip title="편집">
              <Button size="small" icon={<EditOutlined />} onClick={e => { e.stopPropagation(); openEdit(record); }} />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(record.id)}>
              <Tooltip title="삭제">
                <Button size="small" danger icon={<DeleteOutlined />} onClick={e => e.stopPropagation()} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    /* 하단 고정 패딩을 두지 않는다 — 실측 높이 밖에 남아 문서 세로스크롤을 만든다 */
    <div>
      <style>{`
        .trip-row-even td { background: #eef2ff !important; }
        .trip-row-odd  td { background: #f8fafc !important; }
        .trip-row-even:hover td { background: #dde6ff !important; }
        .trip-row-odd:hover  td { background: #edf2f7 !important; }
      `}</style>
      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: gray[800] }}>
            <CarOutlined style={{ marginRight: 8, color: brand.primary }} />업무 및 출장
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>연구원 출장 일정을 캘린더로 관리합니다.</Text>
        </div>
        <Space>
          {canWrite && selectedRowKeys.length >= 2 && (
            <Button
              icon={<MergeOutlined />}
              onClick={handleMerge}
              style={{ borderColor: '#f59e0b', color: '#d97706' }}
            >
              선택 통합 ({selectedRowKeys.length}건)
            </Button>
          )}
          {canWrite && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}
              style={{ background: brand.primary, borderColor: brand.primary }}>
              업무및출장 등록
            </Button>
          )}
        </Space>
      </div>

      {/* 필터 + 뷰 전환 */}
      <Card style={{ marginBottom: 16, borderRadius: 10 }} bodyStyle={{ padding: '12px 16px' }}>
        <Row gutter={[8, 8]} align="middle" wrap>

          {/* 목록 전체 뷰에서만: 년도 + 기간 */}
          {viewMode === 'list' && listGroupMode === 'all' && (
            <>
              <Col>
                <Select
                  value={searchYear}
                  disabled={!!(searchDateRange[0] && searchDateRange[1])}
                  onChange={v => setSearchYear(v)}
                  style={{ width: 88 }}
                >
                  {[2024, 2025, 2026, 2027].map(y => <Option key={y} value={y}>{y}년</Option>)}
                </Select>
              </Col>
              <Col>
                <RangePicker
                  value={searchDateRange as [Dayjs, Dayjs]}
                  onChange={dates => {
                    const range: [Dayjs | null, Dayjs | null] = dates ? [dates[0], dates[1]] : [null, null];
                    setSearchDateRange(range);
                  }}
                  placeholder={['시작일', '종료일']}
                  allowClear
                  style={{ width: 220 }}
                />
              </Col>
            </>
          )}

          {/* 구분 필터 (공통) */}
          <Col>
            <Select
              mode="multiple"
              allowClear
              placeholder="구분"
              value={searchWorkTypes}
              onChange={setSearchWorkTypes}
              style={{ minWidth: 160 }}
              maxTagCount="responsive"
              options={WORK_TYPES.map(t => ({ value: t.value, label: `${t.icon} ${t.label}` }))}
            />
          </Col>

          {/* 연구원 검색 (공통, debounced) */}
          <Col>
            <Input
              prefix={<SearchOutlined />}
              placeholder="연구원 검색"
              value={researcherInput}
              onChange={e => setResearcherInput(e.target.value)}
              allowClear
              onClear={() => setResearcherInput('')}
              style={{ width: 140 }}
            />
          </Col>

          {/* 뷰 전환 (오른쪽 정렬) */}
          <Col flex={1} style={{ textAlign: 'right' }}>
            <Space size={6}>
              {viewMode === 'calendar' && (
                <Segmented
                  value={calSubMode}
                  onChange={v => setCalSubMode(v as 'month' | '1week' | '2week')}
                  options={[
                    { value: 'month', label: '월' },
                    { value: '2week', label: '2주' },
                    { value: '1week', label: '1주' },
                  ]}
                  size="small"
                />
              )}
              {viewMode === 'list' && (
                <Segmented
                  value={listGroupMode}
                  onChange={v => setListGroupMode(v as 'all' | 'monthly' | 'weekly')}
                  options={[
                    { value: 'all', label: '전체' },
                    { value: 'monthly', label: '월별' },
                    { value: 'weekly', label: '주별' },
                  ]}
                  size="small"
                />
              )}
              <Segmented
                value={viewMode}
                onChange={v => {
                  const next = v as 'calendar' | 'list' | 'researcher';
                  setViewMode(next);
                  if (next === 'calendar') {
                    setSearchDateRange([null, null]);
                    setSearchYear(currentMonth.year());
                  }
                  if (next === 'researcher') {
                    setSearchDateRange([null, null]);
                    setSearchYear(researcherWeek.year());
                  }
                }}
                options={[
                  { value: 'calendar', icon: <CalendarOutlined />, label: '달력' },
                  { value: 'list', icon: <UnorderedListOutlined />, label: '목록' },
                  { value: 'researcher', icon: <TeamOutlined />, label: '연구원' },
                ]}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 달력 뷰 — 뷰포트 바닥까지 실측한 높이 안에서만 스크롤 */}
      {viewMode === 'calendar' && (
        <div ref={calWrapRef} style={{ maxHeight: calHeight, overflowY: 'auto' }}>
        <Spin spinning={loading}>
          <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
            {/* 네비게이션 */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', borderBottom: '1px solid #f1f5f9',
            }}>
              <Button
                icon={<LeftOutlined />} type="text"
                onClick={() => {
                  if (calSubMode === 'month') {
                    setCurrentMonth(m => {
                      const next = m.subtract(1, 'month');
                      if (next.year() !== searchYear) setSearchYear(next.year());
                      return next;
                    });
                  } else if (calSubMode === '1week') {
                    setCurrentWeek(w => {
                      const next = w.subtract(1, 'week');
                      if (next.year() !== searchYear) setSearchYear(next.year());
                      return next;
                    });
                  } else {
                    setCurrentWeek(w => {
                      const next = w.subtract(2, 'week');
                      if (next.year() !== searchYear) setSearchYear(next.year());
                      return next;
                    });
                  }
                }}
              />
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: gray[800] }}>
                    {calSubMode === 'month'
                      ? currentMonth.format('YYYY-MM')
                      : calSubMode === '1week'
                        ? `${currentWeek.startOf('week').format('YYYY-MM-DD')} ~ ${currentWeek.endOf('week').format('YYYY-MM-DD')}`
                        : `${currentWeek.startOf('week').format('YYYY-MM-DD')} ~ ${currentWeek.startOf('week').add(13, 'day').format('YYYY-MM-DD')}`
                    }
                  </span>
                  <Button size="small" type="text"
                    style={{ fontSize: 11, color: brand.primary, padding: '0 6px', height: 20 }}
                    onClick={() => {
                      const today = dayjs();
                      setCurrentMonth(today);
                      setCurrentWeek(today.startOf('week'));
                      if (today.year() !== searchYear) setSearchYear(today.year());
                    }}>오늘</Button>
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {calendarTrips.length}건의 출장
                </Text>
              </div>
              <Button
                icon={<RightOutlined />} type="text"
                onClick={() => {
                  if (calSubMode === 'month') {
                    setCurrentMonth(m => {
                      const next = m.add(1, 'month');
                      if (next.year() !== searchYear) setSearchYear(next.year());
                      return next;
                    });
                  } else if (calSubMode === '1week') {
                    setCurrentWeek(w => {
                      const next = w.add(1, 'week');
                      if (next.year() !== searchYear) setSearchYear(next.year());
                      return next;
                    });
                  } else {
                    setCurrentWeek(w => {
                      const next = w.add(2, 'week');
                      if (next.year() !== searchYear) setSearchYear(next.year());
                      return next;
                    });
                  }
                }}
              />
            </div>

            <div style={{ padding: 12 }}>
              <CalendarView
                trips={calendarTrips}
                refDate={calSubMode === 'month' ? currentMonth : currentWeek}
                calSubMode={calSubMode}
                allResearchers={allResearchers}
                onDayClick={(date) => canWrite && openCreate(date)}
                onTripClick={(trip) => { setViewingTrip(trip); setViewModalOpen(true); }}
              />
            </div>

            {/* 연구원 범례 */}
            {allResearchers.length > 0 && (
              <div style={{ padding: '10px 20px 14px', borderTop: '1px solid #f1f5f9' }}>
                <Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>연구원:</Text>
                {allResearchers.slice(0, 10).map(r => (
                  <Tag key={r} color={getResearcherColor(r, allResearchers)} style={{ marginBottom: 4, fontSize: 11 }}>{r}</Tag>
                ))}
              </div>
            )}
          </Card>
        </Spin>
        </div>
      )}

      {/* 목록 뷰 */}
      {viewMode === 'list' && (
        <div ref={cardWrapRef}>
        <Spin spinning={loading}>
          <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
            {/* 월별/주별: 네비게이션 헤더 */}
            {listGroupMode !== 'all' && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px', borderBottom: '1px solid #f1f5f9',
              }}>
                <Button
                  icon={<LeftOutlined />} type="text"
                  onClick={() => {
                    if (listGroupMode === 'monthly') {
                      setCurrentMonth(m => {
                        const next = m.subtract(1, 'month');
                        if (next.year() !== searchYear) setSearchYear(next.year());
                        return next;
                      });
                    } else {
                      setCurrentWeek(w => {
                        const next = w.subtract(1, 'week');
                        if (next.year() !== searchYear) setSearchYear(next.year());
                        return next;
                      });
                    }
                  }}
                />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: gray[800] }}>
                      {listGroupMode === 'monthly'
                        ? currentMonth.format('YYYY-MM')
                        : `${currentWeek.startOf('week').format('YYYY-MM-DD')} ~ ${currentWeek.endOf('week').format('YYYY-MM-DD')}`
                      }
                    </span>
                    <Button size="small" type="text"
                      style={{ fontSize: 11, color: brand.primary, padding: '0 6px', height: 20 }}
                      onClick={() => {
                        const today = dayjs();
                        setCurrentMonth(today);
                        setCurrentWeek(today.startOf('week'));
                        if (today.year() !== searchYear) setSearchYear(today.year());
                      }}>오늘</Button>
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>{listTrips.length}건의 출장</Text>
                </div>
                <Button
                  icon={<RightOutlined />} type="text"
                  onClick={() => {
                    if (listGroupMode === 'monthly') {
                      setCurrentMonth(m => {
                        const next = m.add(1, 'month');
                        if (next.year() !== searchYear) setSearchYear(next.year());
                        return next;
                      });
                    } else {
                      setCurrentWeek(w => {
                        const next = w.add(1, 'week');
                        if (next.year() !== searchYear) setSearchYear(next.year());
                        return next;
                      });
                    }
                  }}
                />
              </div>
            )}

            <div ref={tableWrapRef}>
            <Table
              dataSource={listTrips}
              columns={columns}
              rowKey="id"
              loading={false}
              size="small"
              scroll={{ y: bodyHeight }}
              rowSelection={{
                selectedRowKeys,
                onChange: keys => setSelectedRowKeys(keys),
                getCheckboxProps: record => ({ name: String(record.id) }),
              }}
              rowClassName={record => {
                const groupIdx = dateGroupMap.get(record.id) ?? 0;
                return groupIdx % 2 === 0 ? 'trip-row-even' : 'trip-row-odd';
              }}
              pagination={{
                pageSize: listPageSize,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100', '200'],
                onChange: (_, ps) => setListPageSize(ps),
                showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
              }}
              locale={{ emptyText: <Empty description="출장 기록이 없습니다." /> }}
              style={{ borderRadius: listGroupMode === 'all' ? 12 : 0, overflow: 'hidden' }}
              onRow={record => ({ style: { cursor: 'pointer' }, onClick: () => { setViewingTrip(record); setViewModalOpen(true); } })}
            />
            </div>
          </Card>
        </Spin>
        </div>
      )}

      {/* 연구원 주간 현황 뷰 — 뷰포트 바닥까지 실측한 높이 안에서만 스크롤 */}
      {viewMode === 'researcher' && (
        <div ref={resWrapRef} style={{ maxHeight: resHeight, overflowY: 'auto' }}>
        <Spin spinning={loading}>
          <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
            {/* 주간 네비게이션 */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', borderBottom: '1px solid #f1f5f9',
            }}>
              <Button icon={<LeftOutlined />} type="text" onClick={() => {
                const next = researcherWeek.subtract(1, 'week');
                setResearcherWeek(next);
                if (next.year() !== searchYear) setSearchYear(next.year());
              }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: gray[800] }}>
                    {`${researcherWeek.startOf('week').format('YYYY-MM-DD')} ~ ${researcherWeek.endOf('week').format('YYYY-MM-DD')}`}
                  </span>
                  <span style={{ fontSize: 12, color: gray[500], fontWeight: 500 }}>
                    ({researcherWeek.startOf('week').format('YYYY')}년 {researcherWeek.week()}주차)
                  </span>
                  <Button size="small" type="text"
                    style={{ fontSize: 11, color: brand.primary, padding: '0 6px', height: 20 }}
                    onClick={() => {
                      const today = dayjs();
                      setResearcherWeek(today.startOf('week'));
                      if (today.year() !== searchYear) setSearchYear(today.year());
                    }}>오늘</Button>
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>주간 업무 및 출장 현황</Text>
              </div>
              <Button icon={<RightOutlined />} type="text" onClick={() => {
                const next = researcherWeek.add(1, 'week');
                setResearcherWeek(next);
                if (next.year() !== searchYear) setSearchYear(next.year());
              }} />
            </div>
            <div style={{ padding: 16 }}>
              <ResearcherView
                trips={researcherWeekTrips}
                weekStart={researcherWeek.startOf('week')}
                researchers={masterResearchers.length > 0 ? masterResearchers : allResearchers}
                allResearchers={masterResearchers.length > 0 ? masterResearchers : allResearchers}
                onTripClick={trip => { setViewingTrip(trip); setViewModalOpen(true); }}
              />
            </div>
          </Card>
        </Spin>
        </div>
      )}

      {/* 내용 확인 모달 */}
      {viewingTrip && (
        <Modal
          title={<Space><CarOutlined style={{ color: brand.primary }} /><span>업무및출장 상세</span></Space>}
          open={viewModalOpen}
          onCancel={() => setViewModalOpen(false)}
          footer={
            <Space>
              {canWrite && (
                <Button type="primary" icon={<EditOutlined />}
                  style={{ background: brand.primary, borderColor: brand.primary }}
                  onClick={() => { setViewModalOpen(false); openEdit(viewingTrip); }}>
                  편집
                </Button>
              )}
              {canDelete && (
                <Popconfirm title="삭제하시겠습니까?" onConfirm={async () => { await handleDelete(viewingTrip.id); setViewModalOpen(false); }}>
                  <Button danger icon={<DeleteOutlined />}>삭제</Button>
                </Popconfirm>
              )}
              <Button onClick={() => setViewModalOpen(false)}>닫기</Button>
            </Space>
          }
          width={540}
        >
          {(() => {
            const t = viewingTrip;
            const wt = WORK_TYPES.find(x => x.value === t.work_type);
            const colors = getWorkTypeColor(t.work_type);
            const startD = t.trip_start_date ? dayjs(t.trip_start_date) : null;
            const endD = t.trip_end_date ? dayjs(t.trip_end_date) : null;
            return (
              <div style={{ padding: '4px 0' }}>
                <Row gutter={[0, 12]}>
                  <Col span={24}>
                    <Tag style={{ fontSize: 13, padding: '2px 10px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text }}>
                      {wt?.icon} {t.work_type || '외근'}
                    </Tag>
                    {t.duration && <Tag color={DURATION_COLORS[t.duration] || 'default'} style={{ marginLeft: 6 }}>{t.duration}</Tag>}
                  </Col>
                  <Col span={12}><Text type="secondary" style={{ fontSize: 11 }}>일자</Text><br />
                    <Text strong>{startD?.format('YYYY-MM-DD (ddd)')}{endD && !endD.isSame(startD, 'day') ? ` ~ ${endD.format('YYYY-MM-DD')}` : ''}</Text>
                  </Col>
                  <Col span={12}><Text type="secondary" style={{ fontSize: 11 }}>연구원</Text><br />
                    {t.researchers ? t.researchers.split(/[,，]/).map(r => r.trim()).filter(Boolean).map(r => (
                      <Tag key={r} color={getResearcherColor(r, allResearchers)} style={{ marginBottom: 2 }}>{r}</Tag>
                    )) : '-'}
                  </Col>
                  {t.work_content && (
                    <Col span={24}><Text type="secondary" style={{ fontSize: 11 }}>작업내용</Text><br />
                      <Text>{t.work_content}</Text>
                    </Col>
                  )}
                  {isOutdoorType(t.work_type) && (
                    <>
                      {t.destination && (
                        <Col span={12}><Text type="secondary" style={{ fontSize: 11 }}>출장지</Text><br />
                          <Text><EnvironmentOutlined style={{ color: brand.primary, marginRight: 4 }} />{t.destination}</Text>
                        </Col>
                      )}
                      {t.vehicle_number && (
                        <Col span={12}><Text type="secondary" style={{ fontSize: 11 }}>차량</Text><br />
                          <Text><CarOutlined style={{ marginRight: 4 }} />{t.vehicle_number}</Text>
                        </Col>
                      )}
                      {t.test_number && (
                        <Col span={24}><Text type="secondary" style={{ fontSize: 11 }}>시험번호</Text><br />
                          {t.test_number.split(/[,，]/).map(tn => tn.trim()).filter(Boolean).map(tn => (
                            <Tag key={tn} color="blue" style={{ fontWeight: 600, marginBottom: 2 }}>{tn}</Tag>
                          ))}
                        </Col>
                      )}
                      {t.supervisor_confirmed_at && (
                        <Col span={12}><Text type="secondary" style={{ fontSize: 11 }}>업무배정담당자 확인일</Text><br />
                          <Text>{dayjs(t.supervisor_confirmed_at).format('YYYY-MM-DD')}</Text>
                        </Col>
                      )}
                    </>
                  )}
                  {t.note && (
                    <Col span={24}><Text type="secondary" style={{ fontSize: 11 }}>비고</Text><br />
                      <Text>{t.note}</Text>
                    </Col>
                  )}
                </Row>
              </div>
            );
          })()}
        </Modal>
      )}

      {/* 등록/편집 모달 */}
      <Modal
        title={
          <Space size={6}>
            <CarOutlined style={{ color: brand.primary }} />
            <span style={{ fontWeight: 700 }}>{editingTrip ? '업무및출장 편집' : '업무및출장 등록'}</span>
          </Space>
        }
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={modalWidth.md}
        styles={{ body: { padding: '16px 20px 4px' } }}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>취소</Button>,
          ...(!editingTrip ? [
            <Button key="save-continue" onClick={() => { keepOpenRef.current = true; form.submit(); }}>
              저장 후 계속 등록
            </Button>,
          ] : []),
          <Button key="save" type="primary" onClick={() => form.submit()}
            style={{ background: brand.primary, borderColor: brand.primary }}>
            {editingTrip ? '수정' : '등록'}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ work_type: '외근' }}
          size="middle"
        >
          {/* 구분 */}
          <Form.Item label="구분" name="work_type" rules={[{ required: true }]} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {['out', 'in', 'leave'].map((group, gi) => (
                <React.Fragment key={group}>
                  {WORK_TYPES.filter(t => t.group === group).map(t => {
                    const colors = getWorkTypeColor(t.value);
                    const selected = (watchedWorkType || '외근') === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => form.setFieldValue('work_type', t.value)}
                        style={{
                          padding: '3px 9px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                          border: `1.5px solid ${selected ? colors.border : gray[200]}`,
                          background: selected ? colors.bg : '#fafafa',
                          color: selected ? colors.text : '#64748b',
                          fontWeight: selected ? 700 : 400,
                          transition: 'all 0.12s',
                          lineHeight: '20px',
                        }}
                      >
                        {t.icon} {t.label}
                      </button>
                    );
                  })}
                  {gi < 2 && <div style={{ width: 1, background: gray[200], margin: '0 3px' }} />}
                </React.Fragment>
              ))}
            </div>
          </Form.Item>

          {/* 일정 + 기간 */}
          <Row gutter={8} style={{ marginBottom: 0 }}>
            <Col span={16}>
              <Form.Item label="일정" name="date_range" rules={[{ required: true, message: '날짜를 선택하세요.' }]} style={{ marginBottom: 10 }}>
                <RangePicker style={{ width: '100%' }} placeholder={['시작일', '종료일']} allowEmpty={[false, true]} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="기간" name="duration" style={{ marginBottom: 10 }}>
                <Select placeholder="기간" allowClear>
                  <Option value="종일">종일</Option>
                  <Option value="오전">오전</Option>
                  <Option value="오후">오후</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 연구원 + 차량(외근) */}
          <Row gutter={8}>
            <Col span={isOutdoorType(watchedWorkType) ? 14 : 24}>
              <Form.Item label="연구원" name="researchers" rules={[{ required: true, message: '연구원을 입력하세요.' }]} style={{ marginBottom: 10 }}>
                {masterResearchers.length > 0 ? (
                  <Select mode="tags" placeholder="연구원 선택 또는 입력"
                    options={masterResearchers.map(r => ({ value: r, label: r }))}
                    tokenSeparators={[',']} />
                ) : (
                  <Input prefix={<UserOutlined />} placeholder="예: 유태희, 이대건" />
                )}
              </Form.Item>
            </Col>
            {isOutdoorType(watchedWorkType) && (
              <Col span={10}>
                <Form.Item label="차량" name="vehicle_number" style={{ marginBottom: 10 }}>
                  <Select mode="tags" placeholder="차량 선택"
                    options={masterVehicles.map(v => ({ value: v, label: v }))}
                    tokenSeparators={[',']} />
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* 작업내용 */}
          <Form.Item label="작업내용" name="work_content" style={{ marginBottom: 10 }}>
            <TextArea placeholder="작업 내용 또는 사유" autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>

          {/* 외근 전용 */}
          {isOutdoorType(watchedWorkType) && (
            <>
              <Row gutter={8}>
                <Col span={14}>
                  <Form.Item label="출장지" name="destination" style={{ marginBottom: 10 }}>
                    <Input prefix={<EnvironmentOutlined style={{ color: gray[400] }} />} placeholder="예: 서산, 보령" />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item label="업무배정담당자 확인일" name="supervisor_confirmed_at" style={{ marginBottom: 10 }}>
                    <DatePicker style={{ width: '100%' }} placeholder="확인일" disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="시험번호" style={{ marginBottom: 10 }}>
                <div
                  style={{
                    border: '1px solid #d9d9d9', borderRadius: 6, padding: '4px 8px',
                    minHeight: 34, cursor: 'pointer', display: 'flex', alignItems: 'center',
                    flexWrap: 'wrap', gap: 4, background: '#fff',
                  }}
                  onClick={() => { setTestPickerSearch(''); setTestPickerSelected([...selectedTestNumbers]); setTestPickerOpen(true); }}
                >
                  {selectedTestNumbers.length === 0 ? (
                    <span style={{ color: '#bfbfbf', fontSize: 13 }}>
                      <NumberOutlined style={{ marginRight: 6 }} />클릭하여 시험번호 선택 (다중선택 가능)
                    </span>
                  ) : (
                    <>
                      {selectedTestNumbers.map(tn => (
                        <Tag key={tn} color="blue" closable
                          onClose={e => { e.stopPropagation(); setSelectedTestNumbers(prev => prev.filter(t => t !== tn)); }}
                          style={{ margin: 0 }}>{tn}</Tag>
                      ))}
                      <span style={{ color: brand.primary, fontSize: 12, marginLeft: 2 }}>+ 추가</span>
                    </>
                  )}
                </div>
              </Form.Item>
            </>
          )}

          {/* 반복 등록 (신규만) */}
          {!editingTrip && (
            <Form.Item style={{ marginBottom: 10 }}>
              <div style={{
                background: repeatEnabled ? '#eff6ff' : gray[50],
                border: `1px solid ${repeatEnabled ? '#bfdbfe' : gray[200]}`,
                borderRadius: 8, padding: '7px 12px',
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap',
              }}>
                <Switch
                  checked={repeatEnabled}
                  onChange={setRepeatEnabled}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  size="small"
                />
                <span style={{ fontSize: 13, color: gray[600], fontWeight: 500, flexShrink: 0 }}>반복 등록</span>
                {repeatEnabled && (
                  <>
                    <InputNumber
                      min={2} max={52} value={repeatCount}
                      onChange={v => setRepeatCount(v || 2)}
                      addonAfter="회" style={{ width: 64 }} size="small"
                    />
                    <span style={{ color: gray[400], fontSize: 12, flexShrink: 0 }}>·</span>
                    <InputNumber
                      min={1} max={365} value={repeatInterval}
                      onChange={v => setRepeatInterval(v || 1)}
                      addonAfter="일" style={{ width: 62 }} size="small"
                    />
                    <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {(() => {
                        const dr = form.getFieldValue('date_range');
                        const s = dr?.[0];
                        const end = s ? s.add((repeatCount - 1) * repeatInterval, 'day').format('YYYY-MM-DD') : '?';
                        return `→ 총 ${repeatCount}건 (~${end})`;
                      })()}
                    </Text>
                  </>
                )}
              </div>
            </Form.Item>
          )}

          {/* 비고 */}
          <Form.Item label="비고" name="note" style={{ marginBottom: 4 }}>
            <TextArea placeholder="기타 메모" autoSize={{ minRows: 1, maxRows: 3 }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 시험번호 선택 모달 (다중선택) */}
      <Modal
        title={<Space><NumberOutlined style={{ color: brand.primary }} /><span>시험번호 선택</span></Space>}
        open={testPickerOpen}
        onCancel={() => setTestPickerOpen(false)}
        onOk={() => {
          setSelectedTestNumbers([...testPickerSelected]);
          setTestPickerOpen(false);
        }}
        okText={`적용 (${testPickerSelected.length}건 선택)`}
        cancelText="취소"
        okButtonProps={{ style: { background: brand.primary, borderColor: brand.primary } }}
        width={720}
      >
        <div style={{ marginBottom: 8 }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="시험번호·제목·의뢰사·장소 검색"
            value={testPickerSearch}
            onChange={e => setTestPickerSearch(e.target.value)}
            allowClear
          />
        </div>
        {testPickerSelected.length > 0 && (
          <div style={{ marginBottom: 8, padding: '6px 10px', background: '#eff6ff', borderRadius: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <span style={{ fontSize: 12, color: brand.primary, marginRight: 4 }}>선택됨:</span>
            {testPickerSelected.map(tn => (
              <Tag
                key={tn}
                color="blue"
                closable
                onClose={() => setTestPickerSelected(prev => prev.filter(t => t !== tn))}
                style={{ margin: 0 }}
              >
                {tn}
              </Tag>
            ))}
          </div>
        )}
        <Table
          size="small"
          rowKey="id"
          pagination={{ pageSize: 10, showTotal: t => `총 ${t}건` }}
          scroll={{ y: 340 }}
          onRow={record => ({
            style: {
              cursor: 'pointer',
              background: testPickerSelected.includes(record.test_number) ? '#eff6ff' : undefined,
            },
            onClick: () => {
              const tn = record.test_number;
              if (!tn) return;
              setTestPickerSelected(prev =>
                prev.includes(tn) ? prev.filter(t => t !== tn) : [...prev, tn]
              );
            },
          })}
          dataSource={scheduleList.filter(s => {
            if (!testPickerSearch) return true;
            const q = testPickerSearch.toLowerCase();
            return (
              (s.test_number || '').toLowerCase().includes(q) ||
              (s.title || '').toLowerCase().includes(q) ||
              (s.company_name || '').toLowerCase().includes(q) ||
              (s.location_name || '').toLowerCase().includes(q)
            );
          })}
          columns={[
            {
              title: '',
              width: 36,
              render: (_: any, record: any) => (
                <input
                  type="checkbox"
                  readOnly
                  checked={testPickerSelected.includes(record.test_number)}
                  style={{ cursor: 'pointer' }}
                />
              ),
            },
            { title: '시험번호', dataIndex: 'test_number', width: 140,
              render: (v: string) => <Tag color="blue" style={{ fontWeight: 600 }}>{v}</Tag> },
            { title: '제목', dataIndex: 'title', ellipsis: true },
            { title: '의뢰사', dataIndex: 'company_name', width: 90,
              render: (v: string) => v ? <Tag>{v}</Tag> : '-' },
            { title: '시험장소', dataIndex: 'location_name', width: 100,
              render: (v: string) => v || '-' },
            { title: '상태', dataIndex: 'status', width: 70,
              render: (v: string) => {
                const colorMap: Record<string, string> = { '진행중': 'green', '완료': 'default', '대기': 'orange', '중단': 'red' };
                return v ? <Tag color={colorMap[v] || 'default'}>{v}</Tag> : '-';
              }},
          ]}
        />
      </Modal>
    </div>
  );
};

export default BusinessTripPage;
